import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import { AxiosResponse } from 'axios';
import { Button, Loader, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import useCallbackAsync from 'hooks/useCallbackAsync';
import useCountdown from 'router/subrouters/Auth/hooks/useCountdown';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import PinField from 'router/subrouters/Dash/subrouters/private/subrouters/Profile/pages/ProfilePage/components/PinField';
import { formatPhoneNumber } from 'phoneNumberUtils';
import { required } from 'validations/general';
import {
  NUM_OF_VERIFICATION_PINS,
  RESEND_CODE_COOLDOWN,
  TRANSITION_TIME,
  VERIFIED_PIN_ID,
} from 'data/global';

import './PhoneTransferModal.styles.scss';

type PhoneTransferModalProps = {
  className?: string;
  contactNumber?: string;
  initiate: (contactNumber: string) => Promise<unknown>;
  check: (
    contactNumber: string,
    verificationCode: string,
  ) => Promise<AxiosResponse<any>>;
  onSuccess: (data: any) => void;
  /** Returns true when the error was handled inline by the caller. */
  onServerError: (error: unknown, contactNumber?: string) => boolean;
} & ModalProps;

const PhoneTransferModal: React.ForwardRefRenderFunction<
  ModalRef,
  PhoneTransferModalProps
> = (props, ref) => {
  const {
    className,
    contactNumber,
    initiate,
    check,
    onSuccess,
    onServerError,
    ...rest
  } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('phone-transfer-modal', className);

  const { t } = useTranslation();

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const { timeLeft, startCountdown, clearCountdown } =
    useCountdown(RESEND_CODE_COOLDOWN);

  // PinField keeps its digits in local state, so remount it to clear the boxes
  // between attempts and resends.
  const [attempt, setAttempt] = useState(0);

  const [sendCode, sending] = useCallbackAsync(async () => {
    if (!contactNumber) return;

    try {
      await initiate(contactNumber);
      startCountdown();
    } catch (e) {
      if (onServerError(e, contactNumber)) return;

      popServerError(e);
    }
  }, false);

  const [handleSubmit, checking] = useCallbackAsync(
    async ({ verificationCode }: { verificationCode: string }) => {
      if (!contactNumber) return;

      try {
        const { data } = await check(contactNumber, verificationCode);

        clearCountdown();
        modal?.close();
        onSuccess(data);
      } catch (e) {
        setAttempt((old) => old + 1);

        if (onServerError(e, contactNumber)) return;

        popServerError(e);
      }
    },
    false,
  );

  const onResend = useCallback(async () => {
    setAttempt((old) => old + 1);

    await sendCode();

    popSuccess(t('pleaseCheckMessageInboxOnYourPhone'));
  }, [popSuccess, sendCode, t]);

  useEffect(() => {
    if (!modal?.openState) return;

    setAttempt((old) => old + 1);
    sendCode();

    // Sending the code is driven by the modal opening; re-running it whenever
    // the memoised callbacks change would fire a second SMS.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal?.openState]);

  useEffect(() => {
    if (!modal?.openState) return;

    const timeout = setTimeout(
      () => document.getElementById(VERIFIED_PIN_ID)?.focus(),
      TRANSITION_TIME,
    );

    return () => clearTimeout(timeout);
  }, [modal?.openState, attempt]);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('confirmTheNumber')}
      // Dismissing must leave nothing behind - no account created, no number
      // saved - so closing simply stops the countdown.
      onClose={clearCountdown}
      {...rest}
    >
      {(sending || checking) && <Loader />}

      <Form onSubmit={handleSubmit}>
        {({ handleSubmit, values: { verificationCode } }) => (
          <form onSubmit={handleSubmit}>
            <p>
              {t('enterTheCodeSentTo', {
                number: formatPhoneNumber(contactNumber || ''),
              })}
            </p>

            <Field
              key={attempt}
              name="verificationCode"
              numOfPins={NUM_OF_VERIFICATION_PINS}
              component={PinField}
              validate={required(t('youMustEnterVerificationCode'))}
            />

            <div className="resend">
              {`${t('didNotGetVerificationCode')} `}
              <Button
                variant="link"
                disabled={sending || timeLeft > 0}
                onClick={onResend}
              >
                {timeLeft === 0
                  ? t('resendIt')
                  : t('waitFor', { count: timeLeft })}
              </Button>
            </div>

            <div className="actions">
              <Button type="button" variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>

              <Button
                type="submit"
                disabled={
                  sending ||
                  checking ||
                  verificationCode?.length !== NUM_OF_VERIFICATION_PINS
                }
              >
                {t('confirmCode')}
              </Button>
            </div>
          </form>
        )}
      </Form>
    </Modal>
  );
};

export default forwardRef(PhoneTransferModal);
